import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  TrendingUp,
  Users,
  Calendar,
  Phone,
  FileText,
  IndianRupee,
  Trophy,
  Award,
  ArrowUp,
  ArrowDown,
  Minus,
  RefreshCw,
  Settings,
  Bell,
  Zap,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { SetTargetsModal } from '@/components/targets/SetTargetsModal';
import {
  ExecutiveTarget,
  TargetPeriod,
  LeaderboardEntry,
  TeamOverview,
  PERIOD_OPTIONS,
} from '@/types/targets';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Format currency in Indian format
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Get progress color based on percentage
const getProgressColor = (progress: number) => {
  if (progress >= 100) return 'bg-green-500';
  if (progress >= 80) return 'bg-blue-500';
  if (progress >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

// Get progress text color
const getProgressTextColor = (progress: number) => {
  if (progress >= 100) return 'text-green-600';
  if (progress >= 80) return 'text-blue-600';
  if (progress >= 50) return 'text-yellow-600';
  return 'text-red-600';
};

// Get status badge
const getStatusBadge = (status: string) => {
  const statusConfig: Record<string, { color: string; label: string }> = {
    EXCEEDED: { color: 'bg-green-100 text-green-700', label: 'Exceeded' },
    ON_TRACK: { color: 'bg-blue-100 text-blue-700', label: 'On Track' },
    AT_RISK: { color: 'bg-yellow-100 text-yellow-700', label: 'At Risk' },
    BEHIND: { color: 'bg-red-100 text-red-700', label: 'Behind' }
  };
  const config = statusConfig[status] || statusConfig.BEHIND;
  return <Badge className={config.color}>{config.label}</Badge>;
};

// Animated Counter Component
const AnimatedCounter: React.FC<{ value: number; format?: 'currency' | 'number' }> = ({
  value,
  format = 'number'
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 1000;
    const steps = 30;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  if (format === 'currency') {
    return <>{formatCurrency(displayValue)}</>;
  }
  return <>{displayValue.toLocaleString()}</>;
};

// Live Indicator Component
const LiveIndicator: React.FC = () => (
  <div className="flex items-center gap-2">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
    </span>
    <span className="text-xs text-green-600 font-medium">LIVE</span>
  </div>
);

// Metric Card Component with animations
const MetricCard: React.FC<{
  icon: React.ElementType;
  label: string;
  achieved: number;
  target: number;
  progress: number;
  format: 'currency' | 'number';
  color: string;
  trend?: number;
}> = ({ icon: Icon, label, achieved, target, progress, format, color, trend }) => {
  const colorClasses: Record<string, string> = {
    green: 'text-green-600 bg-green-50',
    blue: 'text-blue-600 bg-blue-50',
    purple: 'text-purple-600 bg-purple-50',
    orange: 'text-orange-600 bg-orange-50',
    indigo: 'text-indigo-600 bg-indigo-50'
  };

  const formattedAchieved = format === 'currency' ? formatCurrency(achieved) : achieved.toLocaleString();
  const formattedTarget = format === 'currency' ? formatCurrency(target) : target.toLocaleString();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="hover:shadow-lg transition-all duration-300 border-l-4" style={{ borderLeftColor: color === 'green' ? '#22c55e' : color === 'blue' ? '#3b82f6' : color === 'purple' ? '#a855f7' : color === 'orange' ? '#f97316' : '#6366f1' }}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-2xl font-bold ${getProgressTextColor(progress)}`}>
                {Math.round(progress)}%
              </span>
              {progress >= 100 && (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">{label}</span>
              {trend !== undefined && trend !== 0 && (
                <span className={`text-xs flex items-center ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {Math.abs(trend)}%
                </span>
              )}
            </div>
            <div className="relative">
              <Progress value={Math.min(progress, 100)} className="h-2" />
              {progress > 100 && (
                <div
                  className="absolute top-0 left-0 h-2 bg-green-400 rounded-full opacity-50"
                  style={{ width: `${Math.min(progress, 150)}%` }}
                />
              )}
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span className="font-medium">{formattedAchieved}</span>
              <span>/ {formattedTarget}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Leaderboard Component
const Leaderboard: React.FC<{ data: LeaderboardEntry[]; onEntryClick?: (userId: string) => void }> = ({ data, onEntryClick }) => {
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Award className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 text-center text-gray-600 font-bold flex items-center justify-center">{rank}</span>;
  };

  return (
    <div className="space-y-3">
      {data.map((entry, index) => (
        <motion.div
          key={entry.user.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className={`flex items-center gap-4 p-3 rounded-lg border transition-all hover:shadow-md ${
            entry.rank === 1 ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200' :
            entry.rank === 2 ? 'bg-gray-50 border-gray-200' :
            entry.rank === 3 ? 'bg-amber-50 border-amber-200' :
            'bg-white border-gray-100'
          } ${onEntryClick ? 'cursor-pointer' : ''}`}
          onClick={() => onEntryClick?.(entry.user.id)}
          role={onEntryClick ? 'button' : undefined}
          aria-label={onEntryClick ? `View ${entry.user.name}'s pipeline` : undefined}
        >
          <div className="w-8 flex justify-center">
            {getRankIcon(entry.rank)}
          </div>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
            {entry.user.name.charAt(0)}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900">{entry.user.name}</p>
            <p className="text-xs text-gray-500">{formatCurrency(entry.liveStats.revenue)}</p>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${getProgressTextColor(entry.overallProgress)}`}>
              {Math.round(entry.overallProgress)}%
            </p>
            <div className="flex items-center gap-1 text-xs">
              {entry.overallProgress >= 100 ? (
                <ArrowUp className="w-3 h-3 text-green-500" />
              ) : entry.overallProgress >= 50 ? (
                <Minus className="w-3 h-3 text-yellow-500" />
              ) : (
                <ArrowDown className="w-3 h-3 text-red-500" />
              )}
              <span className="text-gray-500">{entry.liveStats.leadsCreated} leads</span>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Team Overview Component
const TeamOverviewSection: React.FC<{ data: TeamOverview; onMemberClick?: (userId: string) => void }> = ({ data, onMemberClick }) => {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0 }}>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{data.summary.exceeded}</p>
              <p className="text-sm text-green-600">Exceeded</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{data.summary.onTrack}</p>
              <p className="text-sm text-blue-600">On Track</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">{data.summary.atRisk}</p>
              <p className="text-sm text-yellow-600">At Risk</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-700">{data.summary.behind}</p>
              <p className="text-sm text-red-600">Behind</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Team Members List */}
      <div className="space-y-3">
        {data.members.map((member, index) => (
          <motion.div
            key={member.user.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card
              className={`hover:shadow-md transition-shadow ${onMemberClick ? 'cursor-pointer hover:border-blue-200' : ''}`}
              onClick={() => onMemberClick?.(member.user.id)}
              role={onMemberClick ? 'button' : undefined}
              aria-label={onMemberClick ? `View ${member.user.name}'s performance details` : undefined}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-lg">
                    {member.user.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">{member.user.name}</p>
                      {getStatusBadge(member.status)}
                    </div>
                    <p className="text-sm text-gray-500">{member.user.email}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${getProgressTextColor(member.overallProgress)}`}>
                      {Math.round(member.overallProgress)}%
                    </p>
                    <p className="text-sm text-gray-500">{formatCurrency(member.liveStats.revenue)}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Progress value={Math.min(member.overallProgress, 100)} className="h-2" />
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center text-sm">
                  <div>
                    <p className="font-medium">{member.liveStats.leadsCreated}</p>
                    <p className="text-xs text-gray-500">Leads</p>
                  </div>
                  <div>
                    <p className="font-medium">{member.liveStats.meetingsHeld}</p>
                    <p className="text-xs text-gray-500">Meetings</p>
                  </div>
                  <div>
                    <p className="font-medium">{member.liveStats.followUpsDone}</p>
                    <p className="text-xs text-gray-500">Follow-ups</p>
                  </div>
                  <div>
                    <p className="font-medium">{member.liveStats.proposalsSent}</p>
                    <p className="text-xs text-gray-500">Proposals</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Main Page Component
const TargetDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<TargetPeriod>('MONTHLY');
  const [activeTab, setActiveTab] = useState(
    user?.role === 'CEO' || user?.role === 'GROUP_MANAGER' ? 'team' : 'my-targets'
  );
  const [isSetTargetsOpen, setIsSetTargetsOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const isManager = user?.role === 'CEO' || user?.role === 'GROUP_MANAGER' || user?.role === 'MANAGER';

  // Fetch current target with real-time updates (every 30 seconds)
  const { data: currentTarget, isLoading: loadingTarget, isFetching: fetchingTarget, error: errorTarget, refetch: refetchTarget } = useQuery({
    queryKey: ['current-target', period],
    queryFn: async (): Promise<ExecutiveTarget> => {
      const response = await fetch(`${API_BASE_URL}/targets/current?period=${period}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch target');
      const data = await response.json();
      setLastRefresh(new Date());
      return data.data;
    },
    refetchInterval: 30000, // Real-time: refresh every 30 seconds
    staleTime: 15000 // Consider data stale after 15 seconds
  });

  // Fetch leaderboard with real-time updates
  const { data: leaderboard, isLoading: loadingLeaderboard, error: errorLeaderboard } = useQuery({
    queryKey: ['leaderboard', period],
    queryFn: async (): Promise<LeaderboardEntry[]> => {
      const response = await fetch(`${API_BASE_URL}/targets/leaderboard?period=${period}&limit=10`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch leaderboard');
      const data = await response.json();
      return data.data;
    },
    refetchInterval: 30000
  });

  // Fetch team overview (only for managers) with real-time updates
  const { data: teamOverview, isLoading: loadingTeam, error: errorTeam } = useQuery({
    queryKey: ['team-overview', period],
    queryFn: async (): Promise<TeamOverview> => {
      const response = await fetch(`${API_BASE_URL}/targets/team-overview?period=${period}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch team overview');
      const data = await response.json();
      return data.data;
    },
    enabled: isManager,
    refetchInterval: 30000
  });

  // Error display helper
  const renderError = (error: Error | null, onRetry: () => void) => error ? (
    <Card><CardContent className="p-12 text-center">
      <p className="text-red-600 font-medium">Failed to load data</p>
      <p className="text-sm text-gray-500 mt-1">{error.message}</p>
      <Button variant="outline" className="mt-4" onClick={onRetry}>Retry</Button>
    </CardContent></Card>
  ) : null;

  // Manual refresh all queries
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['current-target'] });
    queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    queryClient.invalidateQueries({ queryKey: ['team-overview'] });
    setLastRefresh(new Date());
  };

  // Format last refresh time
  const formatLastRefresh = () => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - lastRefresh.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Target className="w-8 h-8 text-blue-600" />
              Target Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Track your performance and achieve your goals
              {fetchingTarget && currentTarget && (
                <span className="ml-2 text-xs text-gray-400 animate-pulse">Updating...</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Live Indicator */}
            <LiveIndicator />

            {/* Last Refresh */}
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {formatLastRefresh()}
            </span>

            {/* Period Selector */}
            <Select value={period} onValueChange={(v) => setPeriod(v as TargetPeriod)}>
              <SelectTrigger className="w-40" aria-label="Select target period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Refresh Button */}
            <Button variant="outline" onClick={handleRefresh} aria-label="Refresh dashboard data">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>

            {/* Set Targets Button (Managers only) */}
            {isManager && (
              <Button onClick={() => setIsSetTargetsOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                <Settings className="w-4 h-4 mr-2" />
                Set Targets
              </Button>
            )}
          </div>
        </div>

        {/* Overall Progress Card */}
        <AnimatePresence mode="wait">
          {currentTarget && (
            <motion.div
              key={`progress-${period}`}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white overflow-hidden relative">
                {/* Animated background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
                </div>
                <CardContent className="p-6 relative z-10">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-blue-100 text-sm">Overall Progress - {currentTarget.periodLabel}</p>
                        {currentTarget.overallProgress >= 100 && (
                          <Badge className="bg-green-500 text-white">
                            <Zap className="w-3 h-3 mr-1" />
                            Target Met!
                          </Badge>
                        )}
                      </div>
                      <p className="text-5xl font-bold mt-2">
                        <AnimatedCounter value={Math.round(currentTarget.overallProgress)} />%
                      </p>
                      <p className="text-blue-100 mt-2">
                        {currentTarget.overallProgress >= 100 ? '🎉 Target Exceeded! Keep it up!' :
                         currentTarget.overallProgress >= 80 ? '🔥 Almost there, keep going!' :
                         currentTarget.overallProgress >= 50 ? '💪 Good progress, push harder!' :
                         '⚡ Need to pick up the pace!'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-4 md:gap-6">
                      <div className="text-center">
                        <p className="text-4xl font-bold">
                          <AnimatedCounter value={currentTarget.liveStats?.revenue || 0} format="currency" />
                        </p>
                        <p className="text-blue-100 text-sm">Revenue</p>
                      </div>
                      <div className="text-center">
                        <p className="text-4xl font-bold">
                          <AnimatedCounter value={currentTarget.liveStats?.leadsCreated || 0} />
                        </p>
                        <p className="text-blue-100 text-sm">Leads</p>
                      </div>
                      <div className="text-center">
                        <p className="text-4xl font-bold">
                          <AnimatedCounter value={currentTarget.liveStats?.leadsConverted || 0} />
                        </p>
                        <p className="text-blue-100 text-sm">Conversions</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="my-targets">My Targets</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            {isManager && <TabsTrigger value="team">Team</TabsTrigger>}
          </TabsList>

          {/* My Targets Tab */}
          <TabsContent value="my-targets" className="mt-6">
            {loadingTarget ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : errorTarget ? (
              renderError(errorTarget as Error, () => refetchTarget())
            ) : currentTarget ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <MetricCard
                  icon={IndianRupee}
                  label="Revenue"
                  achieved={currentTarget.liveStats?.revenue || 0}
                  target={currentTarget.revenueTarget}
                  progress={currentTarget.revenueProgress}
                  format="currency"
                  color="green"
                />
                <MetricCard
                  icon={Users}
                  label="Leads Created"
                  achieved={currentTarget.liveStats?.leadsCreated || 0}
                  target={currentTarget.leadsTarget}
                  progress={currentTarget.leadsProgress}
                  format="number"
                  color="blue"
                />
                <MetricCard
                  icon={Calendar}
                  label="Meetings"
                  achieved={currentTarget.liveStats?.meetingsHeld || 0}
                  target={currentTarget.meetingsTarget}
                  progress={calculateProgress(currentTarget.liveStats?.meetingsHeld || 0, currentTarget.meetingsTarget)}
                  format="number"
                  color="purple"
                />
                <MetricCard
                  icon={Phone}
                  label="Follow-ups"
                  achieved={currentTarget.liveStats?.followUpsDone || 0}
                  target={currentTarget.followUpsTarget}
                  progress={calculateProgress(currentTarget.liveStats?.followUpsDone || 0, currentTarget.followUpsTarget)}
                  format="number"
                  color="orange"
                />
                <MetricCard
                  icon={FileText}
                  label="Proposals"
                  achieved={currentTarget.liveStats?.proposalsSent || 0}
                  target={currentTarget.proposalsTarget}
                  progress={calculateProgress(currentTarget.liveStats?.proposalsSent || 0, currentTarget.proposalsTarget)}
                  format="number"
                  color="indigo"
                />
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Target className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 text-lg font-medium">No targets set for this period</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {isManager ? (
                      <>Click "Set Targets" to configure targets for your team</>
                    ) : (
                      <>Contact your manager to set targets</>
                    )}
                  </p>
                  {isManager && (
                    <Button
                      className="mt-4"
                      onClick={() => setIsSetTargetsOpen(true)}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Set Targets Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Performance Leaderboard
                  </div>
                  <LiveIndicator />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingLeaderboard ? (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : errorLeaderboard ? (
                  renderError(errorLeaderboard as Error, handleRefresh)
                ) : leaderboard && leaderboard.length > 0 ? (
                  <Leaderboard data={leaderboard} onEntryClick={(userId) => navigate(`/pipeline?assignedTo=${userId}`)} />
                ) : (
                  <div className="text-center py-12">
                    <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">No leaderboard data available</p>
                    <p className="text-sm text-gray-500 mt-2">Set targets for team members to see the leaderboard</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab (Managers only) */}
          {isManager && (
            <TabsContent value="team" className="mt-6">
              {loadingTeam ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : errorTeam ? (
                renderError(errorTeam as Error, handleRefresh)
              ) : teamOverview ? (
                <TeamOverviewSection data={teamOverview} onMemberClick={(userId) => navigate(`/pipeline?assignedTo=${userId}`)} />
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">No team data available</p>
                    <p className="text-sm text-gray-500 mt-2">Add team members to see their progress</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Set Targets Modal */}
      <SetTargetsModal
        isOpen={isSetTargetsOpen}
        onClose={() => setIsSetTargetsOpen(false)}
        period={period}
        onSuccess={() => {
          handleRefresh();
          setIsSetTargetsOpen(false);
        }}
      />
    </DashboardLayout>
  );
};

// Helper function
const calculateProgress = (achieved: number, target: number) => {
  if (!target || target === 0) return 0;
  return Math.min(Math.round((achieved / target) * 100 * 100) / 100, 999);
};

export default TargetDashboardPage;
